import React, { useState, useRef, useCallback, useEffect } from 'react';
import useFlareStore from '../stores/useFlareStore';
import { useShallow } from 'zustand/shallow';

const FlareMultiSlider = ({ 
  width = 280, 
  height = 280, 
  radius = 100,
  strokeWidth = 24,
  showLabels = true,
  enableEdit = true
}) => {
  const [dragging, setDragging] = useState(null);
  const [dragRange, setDragRange] = useState({ min: 0, max: 100 });
  
  const svgRef = useRef(null);
  const draggingRef = useRef(null);
  const dragRangeRef = useRef({ min: 0, max: 100 });
  const valuesRef = useRef([]);
  const lastValidValue = useRef(null);

  // Get state from store
  const [values, setValues, segmentNames, selectedSegmentIndex, setSelectedSegmentIndex] = useFlareStore(
    useShallow((state) => [
      state.flareValues,
      state.setFlareValues,
      state.segmentNames,
      state.selectedSegmentIndex,
      state.setSelectedSegmentIndex
    ])
  );

  const centerX = width / 2;
  const centerY = height / 2;
  const minGap = 2;

  // Update refs when state changes
  useEffect(() => {
    draggingRef.current = dragging;
    dragRangeRef.current = dragRange;
    valuesRef.current = values;
  }, [dragging, dragRange, values]);

  const getAngle = (value) => (value / 100) * 2 * Math.PI - Math.PI / 2;

  const getPosition = (angle, r = radius) => ({
    x: centerX + r * Math.cos(angle),
    y: centerY + r * Math.sin(angle),
  });

  const isValueBetween = (val, min, max) => {
    if (min < max) return val >= min && val <= max;
    return val >= min || val <= max;
  };

  const handleMouseDown = (index) => {
    if (!enableEdit) return;
    
    setDragging(index);
    lastValidValue.current = values[index];

    const minSegmentSize = 5; // Minimum 5% for each segment
    const prev = values[(index - 1 + values.length) % values.length];
    const next = values[(index + 1) % values.length];

    // Calculate range ensuring minimum segment sizes
    let min = prev + minSegmentSize;
    let max = next - minSegmentSize;

    // Handle wrap-around cases properly
    if (min >= 100) min -= 100;
    if (max < 0) max += 100;

    setDragRange({ min, max });
  };

  const handleSegmentClick = (index) => {
    if (!enableEdit) return;
    // Select the segment when clicked
    setSelectedSegmentIndex(index);
  };

  const handleMouseMove = useCallback((e) => {
    if (draggingRef.current === null) return;

    // Get SVG element to calculate relative position
    const svgElement = svgRef.current;
    if (!svgElement) return;
    
    const rect = svgElement.getBoundingClientRect();
    const x = e.clientX - rect.left - centerX;
    const y = e.clientY - rect.top - centerY;

    let angle = Math.atan2(y, x);
    if (angle < -Math.PI / 2) angle += 2 * Math.PI;

    let value = ((angle + Math.PI / 2) / (2 * Math.PI)) * 100;
    value = (value + 100) % 100;

    // Simpler constraint checking using the pre-calculated drag range
    const currentDragRange = dragRangeRef.current;
    
    // Check if the new value is within the allowed drag range
    if (!isValueBetween(value, currentDragRange.min, currentDragRange.max)) {
      // If outside range, find the closest valid position
      if (currentDragRange.min <= currentDragRange.max) {
        // Normal range (doesn't wrap around)
        value = Math.max(currentDragRange.min, Math.min(currentDragRange.max, value));
      } else {
        // Wrap-around range (min > max)
        if (value > currentDragRange.max && value < currentDragRange.min) {
          // Value is in the forbidden middle zone
          const distToMin = Math.abs(value - currentDragRange.min);
          const distToMax = Math.abs(value - currentDragRange.max);
          value = distToMin < distToMax ? currentDragRange.min : currentDragRange.max;
        }
        // If value is >= min or <= max, it's already valid
      }
    }
    
    lastValidValue.current = value;

    const newValues = [...valuesRef.current];
    newValues[draggingRef.current] = value;
    setValues(newValues);
  }, [setValues, centerX, centerY]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
    lastValidValue.current = null;
  }, []);

  const handleMouseLeave = useCallback(() => {
    setDragging(null);
    lastValidValue.current = null;
  }, []);

  // Add document-level event listeners for dragging
  useEffect(() => {
    if (dragging !== null) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp, { passive: false });
      document.addEventListener('mouseleave', handleMouseLeave, { passive: false });
      document.addEventListener('dragend', handleMouseUp, { passive: false });
      window.addEventListener('blur', handleMouseUp, { passive: false });
      
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      
      const safetyTimeout = setTimeout(() => {
        setDragging(null);
        lastValidValue.current = null;
      }, 10000);
      
      return () => {
        clearTimeout(safetyTimeout);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mouseleave', handleMouseLeave);
        document.removeEventListener('dragend', handleMouseUp);
        window.removeEventListener('blur', handleMouseUp);
        
        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp, handleMouseLeave]);

  // Escape key handler
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && dragging !== null) {
        setDragging(null);
        lastValidValue.current = null;
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [dragging]);

  // Color mapping for flare segments
  const segmentColors = {
    purple: '#B984D9',
    yellow: '#FFD152', 
    orange: '#F19B4A',
    blue: '#3B9FF5',
    violet: '#9093DF'
  };

  // Calculate segments and ensure 5% minimum
  const segments = values.map((value, index) => {
    const prevIndex = (index - 1 + values.length) % values.length;
    const segment = (value - values[prevIndex] + 100) % 100;
    return Math.max(segment, 5);
  });

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {showLabels && (
        <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: '600' }}>Flare Dial</h4>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
            Segments: [{segments.map((s) => s.toFixed(1)).join(', ')}]
          </p>
        </div>
      )}

      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ display: 'block' }}
      >
        {/* Background circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
        />

        {/* Segments with visual gaps */}
        {segments.map((segment, index) => {
          if (segment <= 0) return null;

          const startValue = index === 0 ? values[values.length - 1] : values[index - 1];
          const endValue = values[index];

          const gapSize = 2.2;
          const displayStartValue = (startValue + gapSize) % 100;
          const displayEndValue = (endValue - gapSize + 100) % 100;

          const startAngle = getAngle(displayStartValue);
          const endAngle = getAngle(displayEndValue);

          const startPos = getPosition(startAngle);
          const endPos = getPosition(endAngle);

          let sweep = endAngle - startAngle;
          if (sweep < 0) sweep += 2 * Math.PI;

          const largeArcFlag = sweep > Math.PI ? 1 : 0;
          const colorName = segmentNames[index] || Object.keys(segmentColors)[index] || 'purple';
          const color = segmentColors[colorName] || segmentColors.purple;
          const isSelected = selectedSegmentIndex === index;

          return (
            <path
              key={index}
              d={`M ${startPos.x} ${startPos.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endPos.x} ${endPos.y}`}
              fill="none"
              stroke={color}
              strokeWidth={isSelected ? strokeWidth + 2 : strokeWidth}
              strokeLinecap="round"
              style={{ cursor: enableEdit ? 'pointer' : 'default' }}
              onClick={() => handleSegmentClick(index)}
            />
          );
        })}

        {/* Triangle handles */}
        {values.map((value, index) => {
          const angle = getAngle(value);
          const r = radius + 20;
          const size = 13;

          const cx = centerX + r * Math.cos(angle);
          const cy = centerY + r * Math.sin(angle);

          const rotateDeg = (angle * 180) / Math.PI - 90;
          
          // Get the color for this triangle based on its segment
          const colorName = segmentNames[index] || Object.keys(segmentColors)[index] || 'purple';
          const segmentColor = segmentColors[colorName] || segmentColors.purple;
          const isSelected = selectedSegmentIndex === index;

          const trianglePath = `
            M ${cx} ${cy - size / 2}
            L ${cx + size / 2} ${cy + size / 2}
            L ${cx - size / 2} ${cy + size / 2}
            Z
          `;

          return (
            <path
              key={index}
              d={trianglePath}
              transform={`rotate(${rotateDeg} ${cx} ${cy})`}
              fill={segmentColor}
              stroke="#ffffff"
              strokeWidth="1.5"
              onMouseDown={() => handleMouseDown(index)}
              onClick={() => handleSegmentClick(index)}
              style={{ 
                cursor: enableEdit ? 'grab' : 'default'
              }}
            />
          );
        })}
      </svg>
    </div>
  );
};

export default FlareMultiSlider;